import type { WorkLog } from '../types';

/**
 * D-319 sec.9 — the shared "קבץ לפי" model of the work-log views (the work-logs
 * page and the hours tabs). Pure: no React, no i18n; the screen gives the
 * labels for the empty values.
 */
export const GROUP_BYS = ['none', 'customer', 'project', 'engagement', 'worker', 'day', 'task'] as const;
export type GroupBy = (typeof GROUP_BYS)[number];

/** The log fields the grouping reads (a superset of the list rows). */
export type GroupableLog = Pick<WorkLog,
  'id' | 'duration_minutes' | 'start_time' | 'agent_id' | 'agent_name' | 'ticket_id' | 'ticket_title' | 'organization_id' | 'organization_name' | 'customer_name' | 'project_id'
> & { project_name?: string | null; engagement_id?: string | null; engagement_name?: string | null; month_approved?: boolean | null };

export interface LogGroup<L extends GroupableLog = GroupableLog> {
  key: string;
  /** '' = the "none" bucket - the screen prints its own label. */
  label: string;
  logs: L[];
  minutes: number;
  /** true = the logs that have no value for this grouping ("ללא"). */
  isEmpty: boolean;
}

/** Israel-local 'YYYY-MM-DD' of a timestamp (the day the log belongs to). */
export function israelDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** The employee confirmed the month of this log; a missing field = not confirmed (default false). */
export function isMonthApproved(log: { month_approved?: boolean | null }): boolean {
  return log.month_approved === true;
}

function keyAndLabel(l: GroupableLog, by: GroupBy): { key: string; label: string } {
  switch (by) {
    case 'customer': return { key: l.organization_id ?? '', label: l.customer_name || l.organization_name || '' };
    case 'project': return { key: l.project_name ?? '', label: l.project_name ?? '' };
    case 'engagement': return { key: l.engagement_id ?? '', label: l.engagement_name ?? '' };
    case 'worker': return { key: l.agent_id ?? '', label: l.agent_name ?? '' };
    case 'day': { const d = israelDay(l.start_time); return { key: d, label: d }; }
    case 'task': return { key: l.ticket_id ?? '', label: l.ticket_title ?? '' };
    default: return { key: '', label: '' };
  }
}

/**
 * Groups in order of first appearance (the list's own order is kept inside a
 * group); logs without the field fall in one "ללא" group at the end.
 */
export function groupLogs<L extends GroupableLog>(logs: L[], by: GroupBy): LogGroup<L>[] {
  if (by === 'none') return [{ key: '', label: '', logs, minutes: sumMinutes(logs), isEmpty: false }];
  const order: string[] = [];
  const map = new Map<string, LogGroup<L>>();
  let empty: LogGroup<L> | null = null;
  for (const l of logs) {
    const { key, label } = keyAndLabel(l, by);
    if (!key) {
      empty ??= { key: '', label: '', logs: [], minutes: 0, isEmpty: true };
      empty.logs.push(l);
      empty.minutes += l.duration_minutes ?? 0;
      continue;
    }
    let g = map.get(key);
    if (!g) { g = { key, label, logs: [], minutes: 0, isEmpty: false }; map.set(key, g); order.push(key); }
    g.logs.push(l);
    g.minutes += l.duration_minutes ?? 0;
  }
  const groups = order.map((k) => map.get(k)!);
  return empty ? [...groups, empty] : groups;
}

export function sumMinutes(logs: { duration_minutes?: number | null }[]): number {
  return logs.reduce((s, l) => s + (l.duration_minutes ?? 0), 0);
}
